import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { CalendarCellProps } from "@tql/ui-components-react/kendo/dateinputs";
import {
	Calendar,
	CalendarCell,
} from "@tql/ui-components-react/kendo/dateinputs";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import { DatePicker } from "@tql/ui-components-react/tql/dateinputs";
import { TextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { infoCircle } from "@tql/ui-components-react/untitled/icons";
import { useContext, useEffect, useMemo, useRef, useState } from "react";

import type { SelectedAddressBookItems } from "~/features/quote/context/quote-address-context.tsx";
import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useFilteredHolidays } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { useSelectedCarrier } from "~/features/quote/hooks/use-quote-hooks.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type { Holiday } from "~/shared/schemas/holiday-schema.ts";
import type { SubGroupKey } from "~/shared/types/quote-details-types.ts";
import {
	getMinShipDate,
	isHoliday,
	isPastShipDate,
	isWeekend,
} from "~/shared/utils/date-helpers.ts";
import { formatDateToLocalISOString } from "~/shared/utils/string-fns.ts";
import { parseDateString } from "~/shared/utils/time-utils.ts";

import { getAddressTypeLabel } from "../utils/address-helpers.ts";
import styles from "./name-and-date-row.module.css";

type NameAndDateRowProps = {
	shipmentDateReadOnly?: boolean;
	type: SubGroupKey;
	hasFieldError: (field: string) => boolean;
	holidays?: Holiday[];
	onClearFieldError: (field: string) => void;
};

const NAME_MAX_LENGTH = 50;

export function NameAndDateRow({
	shipmentDateReadOnly = false,
	type,
	hasFieldError,
	holidays,
	onClearFieldError,
}: Readonly<NameAndDateRowProps>) {
	const { selectedAddressBookItems, updateSelectedAddressBookItem } =
		useQuoteAddressContext();
	const addressBookItem =
		selectedAddressBookItems[type as keyof SelectedAddressBookItems];
	const { quoteState, onChange } = useContext(QuoteDetailsContext);
	const { isInternal } = useDeploymentType();
	const addressLabel = getAddressTypeLabel(type);
	const isPickup = type === "pickup";
	const [nameValue, setNameValue] = useState(
		addressBookItem?.companyName ?? "",
	);
	const isUserInputRef = useRef(false);

	useEffect(() => {
		if (!isUserInputRef.current) {
			setNameValue(addressBookItem?.companyName ?? "");
		}
		isUserInputRef.current = false;
	}, [addressBookItem?.companyName]);

	const nameLength = nameValue.length;
	const isNameOverLimit = nameLength > NAME_MAX_LENGTH;
	const shipmentDateDate = useMemo(
		() => parseDateString(quoteState.shipmentDate),
		[quoteState.shipmentDate],
	);

	const minShipDate = useMemo(() => getMinShipDate(isInternal), [isInternal]);
	const isShipDateInPast = isPastShipDate(shipmentDateDate, isInternal);

	const pickupCountry = useMemo(() => {
		// Holidays are always determined by the pickup location
		return (
			quoteState.pickupCountry.abbreviation ||
			quoteState.pickupPostalCode.city?.state?.countryCode
		);
	}, [quoteState.pickupCountry, quoteState.pickupPostalCode]);

	const filteredHolidays = useFilteredHolidays(holidays, pickupCountry);

	const isHolidayOrWeekend = (date: Date) => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const isBeforeToday = !isInternal && date < today;

		return (
			isHoliday(date, filteredHolidays) || isWeekend(date) || isBeforeToday
		);
	};

	const CustomCalendarCell = (props: CalendarCellProps) => {
		const { value: date, formattedValue, ...others } = props;
		const isDisabled = isHolidayOrWeekend(date);

		return (
			<CalendarCell
				{...others}
				formattedValue={formattedValue}
				isDisabled={isDisabled}
				value={date}
			/>
		);
	};

	const CustomCalendar = (props: React.ComponentProps<typeof Calendar>) => (
		<Calendar {...props} cell={CustomCalendarCell} />
	);

	const selectedCarrier = useSelectedCarrier(quoteState);

	const carrierExpirationDate = useMemo(
		() => parseDateString(selectedCarrier?.carrierQuoteExpirationDate ?? null),
		[selectedCarrier?.carrierQuoteExpirationDate],
	);

	const nameErrorMessage = isNameOverLimit
		? "Cannot exceed character limit."
		: "Required";
	const showNameError = isNameOverLimit || hasFieldError(`${type}Name`);
	const showExpirationWarning =
		isPickup &&
		!!carrierExpirationDate &&
		!!shipmentDateDate &&
		shipmentDateDate > carrierExpirationDate;

	let shipDateErrorMessage = "";
	if (isShipDateInPast) {
		shipDateErrorMessage = "Ship Date cannot be in the past.";
	} else if (hasFieldError("pickupShipmentDate")) {
		shipDateErrorMessage = "Required";
	}

	return (
		<>
			<div className={"pickup-delivery-row"}>
				<div className={`pickup-delivery-name-wrapper ${styles.nameWrapper}`}>
					<Label editorId={`${type}Name`}>
						{addressLabel} Name <span className={"required"}>*</span>
					</Label>
					<TextBox
						id={`${type}Name`}
						name={`${type}Name`}
						onChange={(e) => {
							const value = e.target.value as string;
							isUserInputRef.current = true;
							setNameValue(value);
							onClearFieldError(`${type}Name`);
							updateSelectedAddressBookItem(type, "companyName", value);
						}}
						valid={!showNameError}
						value={nameValue}
					/>
					<div className={styles.nameFieldMessages}>
						{showNameError ? <Error>{nameErrorMessage}</Error> : <span />}
						{nameLength > 0 ? (
							<span
								className={`${styles.charCounter} ${
									isNameOverLimit ? styles.charCounterOverLimit : ""
								}`}
							>
								{nameLength}/{NAME_MAX_LENGTH}
							</span>
						) : null}
					</div>
				</div>
				{isPickup ? (
					<div className={"pickup-delivery-ship-date-wrapper"}>
						<Label editorId={`${type}ShipmentDate`}>
							Ship Date <span className={"required"}>*</span>
						</Label>
						<DatePicker
							calendar={CustomCalendar}
							disabled={shipmentDateReadOnly}
							id={`${type}ShipmentDate`}
							min={minShipDate}
							name={`${type}ShipmentDate`}
							onChange={(e) => {
								if (e.value) {
									onClearFieldError("pickupShipmentDate");
									onClearFieldError("pickupShipmentDatePast");
									onChange("shipmentDate", formatDateToLocalISOString(e.value));
								} else {
									onChange("shipmentDate", "");
								}
							}}
							valid={!hasFieldError("pickupShipmentDate") && !isShipDateInPast}
							value={shipmentDateDate}
							width={132}
						/>
						{shipDateErrorMessage ? (
							<Error>{shipDateErrorMessage}</Error>
						) : null}
					</div>
				) : null}
			</div>
			{showExpirationWarning ? (
				<div
					className={styles.expirationWarning}
					data-testid={"shipment-date-expiration-warning"}
					role={"alert"}
				>
					<SvgIcon
						className={styles.expirationWarningIcon}
						icon={infoCircle}
						size={"medium"}
					/>
					<p className={styles.expirationWarningText}>
						Please be advised that pricing may change if booking beyond the
						carrier&apos;s rate expiration date.
					</p>
				</div>
			) : null}
		</>
	);
}
